import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { cachedDataItemSet } from "../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../redux/reducers/cachedDataReducer";
import { useUnderwritingHook } from "./useUnderwritingHook";

const KEY = CACHED_DATA_REDUCER_TYPES.LOAN_PIPELINE;

// Normalize an originator / company name for matching loans to correspondents.
export const normOrg = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

// Loans attributed to a correspondent = pipeline loans whose `originator`
// matches the correspondent's company name.
export const filterLoansByOriginator = (pipeline, companyName) => {
  const target = normOrg(companyName);
  if (!target) return [];
  return (Array.isArray(pipeline) ? pipeline : []).filter(
    (l) => normOrg(l?.originator) === target,
  );
};

// { normalizedOriginator: loanCount } from a pipeline array.
export const buildLoanCountsByOriginator = (pipeline) => {
  const counts = {};
  (Array.isArray(pipeline) ? pipeline : []).forEach((loan) => {
    const k = normOrg(loan?.originator);
    if (!k) return;
    counts[k] = (counts[k] || 0) + 1;
  });
  return counts;
};

/**
 * Cached full loan pipeline (state.cachedData.loanPipeline).
 *
 * Sourced from GET /underwriting/underwriting_pipeline. The pipeline is fetched
 * once and reused for the correspondents screens
 * and the header loan search. It's invalidated (set to null) by the cachedData
 * reducer whenever a loan is created or a drilldown's data is updated, so the
 * next consumer re-fetches fresh data.
 *
 * - `auto: true` fetches on mount when not cached (use on screens that need it
 *   immediately). Leave false for the always-mounted header search modal, which
 *   calls `ensure()` only when opened.
 */
export const useCachedLoanPipeline = ({ auto = false } = {}) => {
  const dispatch = useDispatch();
  const cached = useSelector((s) => s.cachedData?.[KEY]);
  const { getUnderwritingPipeline } = useUnderwritingHook();
  const inFlight = useRef(false);

  const ensure = useCallback(() => {
    if (Array.isArray(cached) || inFlight.current) return;
    inFlight.current = true;
    getUnderwritingPipeline({
      onSuccessFn: (data) =>
        dispatch(cachedDataItemSet(KEY, Array.isArray(data) ? data : [])),
      onCompleteFn: () => {
        inFlight.current = false;
      },
      onFailFn: () => {
        inFlight.current = false;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cached]);

  useEffect(() => {
    if (auto) ensure();
  }, [auto, ensure]);

  return {
    pipeline: Array.isArray(cached) ? cached : [],
    isLoaded: Array.isArray(cached),
    ensure,
  };
};

export default useCachedLoanPipeline;
